import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import { fetchIdibuIntegration } from "../../../Redux/ActionCreators/idibuAction";
import {
  ContactGappeoModal,
  IdibuConnectForm,
  IdibuSettingsPanel,
  isIdibuAccountSaved,
} from "./IdibuConnect";
import FeatureNotAvailable, { isFreePlan } from "./FeatureNotAvailable";
import "./integrations.scss";

/** Settings > Integration > idibu: connect, test, update or disconnect the organization's idibu account. */
const IdibuIntegration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedTenant } = useSelector((state) => state.users);
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState(null);
  const [showContact, setShowContact] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(fetchIdibuIntegration());
    if (result?.success) {
      setIntegration(result.data);
      setLoadError("");
    } else {
      setLoadError(result?.message || "Failed to load idibu settings");
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const freePlan = isFreePlan(selectedTenant);
  const merge = (data) => setIntegration((prev) => ({ ...prev, ...data }));
  const canManage = Boolean(integration?.can_manage);

  const handleConnected = (data) => {
    merge(data);
    setNotice(
      data?.status === "active"
        ? { severity: "success", text: "idibu connected. Add the webhook URL below in idibu to receive applicants." }
        : { severity: "warning", text: data?.last_error || "idibu details saved, but they could not be verified yet." }
    );
  };

  const renderConnect = () => {
    if (!canManage) {
      return (
        <div className="idibu-card">
          <p>idibu is not connected for your organization. Ask your admin to connect it.</p>
        </div>
      );
    }
    return (
      <div className="idibu-card">
        <h4>Connect your idibu account</h4>
        <IdibuConnectForm integration={null} onSaved={handleConnected} submitLabel="Connect" />
        <div className="idibu-contact-row">
          {integration?.request_pending ? (
            <span className="idibu-success-text">Your request has been sent. The Gappeo team will contact you.</span>
          ) : (
            <>
              <span className="idibu-muted">Don't have an idibu account?</span>
              <button type="button" className="idibu-link" onClick={() => setShowContact(true)}>
                Contact Gappeo
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  let body;
  if (freePlan) {
    body = (
      <div className="idibu-card">
        <FeatureNotAvailable />
      </div>
    );
  } else if (loading) {
    body = (
      <div className="spinner-border" role="status" style={{ color: "#F75891" }}>
        <span className="visually-hidden">Loading…</span>
      </div>
    );
  } else if (loadError) {
    body = <Alert severity="error">{loadError}</Alert>;
  } else if (!integration?.platform_enabled) {
    body = (
      <div className="idibu-card">
        <p>Job board posting through idibu isn't available yet. Please contact Gappeo to enable it.</p>
      </div>
    );
  } else if (isIdibuAccountSaved(integration)) {
    body = (
      <>
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} className="integrations-notice">
            {notice.text}
          </Alert>
        ) : null}
        <IdibuSettingsPanel
          integration={integration}
          canManage={canManage}
          onChange={(data) => {
            setNotice(null);
            merge(data);
          }}
        />
      </>
    );
  } else {
    body = renderConnect();
  }

  return (
    <div className="job-table-container settings-fill-page">
      <div className="top-navigation">
        <button type="button" className="integrations-back-link" onClick={() => navigate("/integrations")}>
          ← Back to Integration
        </button>
        <div className="createjob-tab">
          <h4>idibu Integration</h4>
        </div>
      </div>
      <div className="table-content" style={{ background: "#FFFFFF" }}>
        <div className="job-settings integrations-scroll">
          <p className="integrations-intro">
            Post jobs to job boards and receive applicants through your organization's own idibu account. Once
            connected, users with the Post to Job Boards permission see a "Post to Job Boards" tab on each job.
          </p>
          {body}
        </div>
      </div>
      {showContact ? (
        <ContactGappeoModal
          onClose={() => setShowContact(false)}
          onSent={() => merge({ request_pending: true })}
        />
      ) : null}
    </div>
  );
};

export default IdibuIntegration;
